import type { CheckedState } from '@radix-ui/react-checkbox';
import { Search, Users, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { queryClient } from '~app/query-client';

import { useGetStudentsByClassIdActive } from '~entities/classes/api/get-enrolled-students-by-class-id-active';
import { useGetAllStudentsWithSearch } from '~entities/student';
import { useGetStudioDetailsAll } from '~entities/studios/api/get-studio-details-all';

import { ClassSelector } from '~features/payment/ui/class-selector';
import { SelectByTagTab } from '~features/payment/ui/select-by-tag-tab';
import { SelectStudentByGroupTab } from '~features/payment/ui/select-student-by-group-tab';
import {
  parseStudentSelectionMode,
  STUDENT_SELECTION_MODE_OPTIONS,
} from '~features/payment/ui/student-selection-step.helpers';
import { STUDENT_SELECTION_MODE } from '~features/payment/ui/student-selection-step.types';
import type { StudentSelectionStepProps } from '~features/payment/ui/student-selection-step.types';
import { SessionSelector } from '~features/shared/session-selector';

import type { Student } from '~pages/payments/bulk-charge-students';

import { KEYS } from '~shared/api/query-keys';
import useDebounce from '~shared/hooks/useDebounce';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

export function StudentSelectionStep({
  onStudentsSelected,
  onFamiliesSelected,
}: StudentSelectionStepProps) {
  const [selectionMode, setSelectionMode] = useState(
    STUDENT_SELECTION_MODE.MANUAL,
  );
  const [selectedStudents, setSelectedStudents] = useState<Array<Student>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);
  const [selectedSession, setSelectedSession] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);

  // const { data: classesData } = useGetAllClassesWithSearch({
  //   sessionId: selectedSession?.value,
  // });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);

  const [allStudents, setAllStudents] = useState<Array<Student>>([]);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [showOnlyActiveStudents, setShowOnlyActiveStudents] = useState(false);

  const observer = useRef<IntersectionObserver>();
  const lastStudentElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasMoreStudents) {
            setCurrentPage((prevPage) => prevPage + 1);
          }
        },
        { rootMargin: '100px' },
      );

      if (node) observer.current.observe(node);
    },
    [hasMoreStudents],
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  const {
    data: studentsData,
    isLoading,
    isFetching,
  } = useGetAllStudentsWithSearch({
    page: currentPage,
    limit: 10,

    name: debouncedSearchTerm,
    ...(showOnlyActiveStudents && { isActive: 'true' }),
    // familyName: selectedFamilyName ? selectedFamilyName : undefined,
    // costumeSize:
    //   selectedCostumeSize !== 'All' ? selectedCostumeSize : undefined,
  });

  const { data: activeStudentsData } = useGetStudentsByClassIdActive({
    classId: selectedClass?.value,
  });

  const { data: studioAllData } = useGetStudioDetailsAll();

  useEffect(() => {
    setCurrentPage(1);
    setAllStudents([]);
    setHasMoreStudents(true);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (studentsData?.data.students) {
      const newStudents = studentsData.data.students.map((student) => ({
        _id: student._id,
        name: student.name,
        familyName: student.familyName ?? '',
      }));

      if (currentPage === 1) {
        setAllStudents(newStudents);
      } else {
        setAllStudents((prev) => {
          const existingIds = new Set(prev.map((s) => s._id));
          const uniqueNew = newStudents.filter((s) => !existingIds.has(s._id));

          return [...prev, ...uniqueNew];
        });
      }

      setHasMoreStudents(currentPage < studentsData.pagination.totalPages);
    }
  }, [studentsData, currentPage]);

  useEffect(() => {
    if (activeStudentsData) {
      setSelectedStudents((prev) => {
        const classStudents = activeStudentsData.map((s) => ({
          _id: s._id,
          name: s.name,
          familyName: s.familyName,
        }));

        const existingIds = new Set(prev.map((s) => s._id));
        const merged = [
          ...prev,
          ...classStudents.filter((s) => !existingIds.has(s._id)),
        ];
        return merged;
      });
    }
  }, [activeStudentsData]);

  // const students =
  //   studentsData?.data.students.map((student) => ({
  //     _id: student._id,
  //     name: student.name,
  //     familyName: student.familyName ?? '',
  //   })) ?? [];

  const handleSelectStudent = (student: Student) => {
    setSelectedStudents((prev) => {
      const isSelected = prev.some((s) => s._id === student._id);
      if (isSelected) {
        return prev.filter((s) => s._id !== student._id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleRemoveStudent = (student: Student, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedStudents((prev) => prev.filter((s) => s._id !== student._id));
  };

  const handleClassChange = (
    value:
      | {
          label: string;
          value: string;
        }
      | undefined
      | null,
  ) => {
    setSelectedClass(value);
    setSelectedStudents(
      activeStudentsData?.map((s) => {
        return {
          _id: s._id,
          name: s.name,
          familyName: s.familyName,
        };
      }) ?? [],
    );
  };

  const handleContinue = () => {
    if (selectedStudents.length > 0) {
      onStudentsSelected(selectedStudents);
    }
  };

  const handleSelectionModeChange = (value: string) => {
    try {
      setSelectionMode(parseStudentSelectionMode(value));
      setSelectedStudents([]);
      setSelectedClass(null);
      setSelectedSession(null);
    } catch (error) {
      console.error('Failed to change student selection mode', {
        value,
        error,
      });
      throw error;
    }
  };

  const handleShowOnlyActiveStudentsChange = (checked: CheckedState) => {
    setShowOnlyActiveStudents(checked === 'indeterminate' ? false : checked);
    setCurrentPage(1);
    setAllStudents([]);
    setSelectedStudents([]);
    void queryClient.invalidateQueries({
      predicate(query) {
        return (
          query.queryKey[0] === KEYS.STUDENTS &&
          query.queryKey[1] === KEYS.SEARCH
        );
      },
    });
  };

  return (
    <div className='space-y-6'>
      <Tabs
        value={selectionMode}
        className='w-full'
        onValueChange={handleSelectionModeChange}
      >
        <TabsList className='grid h-auto w-full grid-cols-2 gap-2 bg-transparent p-0 lg:grid-cols-4 lg:gap-0 lg:bg-muted lg:p-1'>
          {STUDENT_SELECTION_MODE_OPTIONS.map((option) => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              className='h-auto whitespace-normal bg-muted px-2 py-2.5 text-center text-sm lg:bg-transparent lg:py-1'
            >
              <span className='lg:hidden'>{option.shortLabel}</span>
              <span className='hidden lg:inline'>{option.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value={STUDENT_SELECTION_MODE.MANUAL}
          className='space-y-4'
        >
          <div className='relative'>
            <Search className='absolute left-2 top-2.5 size-4 text-muted-foreground' />
            <Input
              placeholder='Search students...'
              className='pl-8'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
          </div>

          <div className='flex flex-col space-y-3'>
            <div className='flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between'>
              <h3 className='text-sm font-medium text-slate-700'>
                All Students
              </h3>
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:space-x-2'>
                <Label className='flex items-start gap-2 sm:items-center'>
                  <Checkbox
                    checked={showOnlyActiveStudents}
                    onCheckedChange={handleShowOnlyActiveStudentsChange}
                  />
                  <span className='leading-snug'>
                    Show Only Active Students (
                    {studioAllData?.activeStudentCount ?? '0'})
                  </span>
                </Label>

                {selectedStudents.length > 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-7 w-fit px-2 text-xs'
                    onClick={() => {
                      setSelectedStudents([]);
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </div>

            <ScrollArea className='h-[300px] rounded-md border'>
              <div className='p-2'>
                {allStudents.length === 0 && !isLoading ? (
                  <p className='py-4 text-center text-muted-foreground'>
                    No students found
                  </p>
                ) : (
                  <>
                    {allStudents.map((student, index) => {
                      const isLast = allStudents.length === index + 1;
                      return (
                        <div
                          key={student._id}
                          ref={isLast ? lastStudentElementRef : null}
                          className='flex cursor-pointer items-center space-x-3 rounded-md px-2 py-1.5 hover:bg-muted'
                          onClick={() => {
                            handleSelectStudent(student);
                          }}
                        >
                          <Checkbox
                            checked={selectedStudents.some(
                              (s) => s._id === student._id,
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onCheckedChange={() => {
                              handleSelectStudent(student);
                            }}
                            className='size-4'
                          />
                          <div className='flex flex-1 items-center justify-between'>
                            <p className='text-sm font-medium capitalize leading-none'>
                              {student.name}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                    {(isLoading || isFetching) && (
                      <div className='flex items-center justify-center py-4'>
                        <div className='size-6 animate-spin rounded-full border-b-2 border-blue-500'></div>
                        <span className='ml-2 text-sm text-muted-foreground'>
                          {currentPage === 1
                            ? 'Loading students...'
                            : `Loading page ${currentPage}...`}
                        </span>
                      </div>
                    )}

                    {!hasMoreStudents &&
                      allStudents.length > 0 &&
                      !isLoading && (
                        <div className='py-4 text-center'>
                          <p className='text-sm text-muted-foreground'>
                            No more students to load
                          </p>
                        </div>
                      )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent
          value={STUDENT_SELECTION_MODE.CLASS}
          className='space-y-4'
        >
          {/* Session Selector */}
          <div className='space-y-1.5'>
            <label
              htmlFor='session-selector'
              className='text-sm font-medium text-slate-700'
            >
              Session
            </label>
            <SessionSelector
              session={selectedSession}
              onChange={setSelectedSession}
              excludePastSessions
            />
          </div>

          {/* Class Selector */}
          <div className='space-y-1.5'>
            <label
              htmlFor='class-selector'
              className='text-sm font-medium text-slate-700'
            >
              Class
            </label>
            <ClassSelector
              selectedSessionId={selectedSession?.value}
              selectedClass={selectedClass}
              onChange={handleClassChange}
            />
          </div>

          {selectedClass && (
            <div className='mt-4 space-y-2'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <Users className='size-5 text-muted-foreground' />
                  <span className='text-sm font-medium'>
                    {selectedStudents.length} students selected from{' '}
                    {/* {mockClasses.find((c) => c.value === selectedClass)?.label} */}
                  </span>
                </div>
                {selectedStudents.length > 0 && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-7 px-2 text-xs'
                    onClick={() => {
                      setSelectedStudents([]);
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </div>

              <ScrollArea className='h-[200px] rounded-md border bg-slate-50'>
                <div className='p-2'>
                  {selectedStudents.map((student) => (
                    <div
                      key={student._id}
                      className='group flex items-center space-x-3 rounded-md px-2 py-1.5 hover:bg-white'
                    >
                      <div className='flex flex-1 items-center justify-between'>
                        <p className='text-sm font-medium leading-none'>
                          {student.name}
                        </p>
                        <div className='flex items-center'>
                          {/* <span className='mr-2 text-xs text-muted-foreground'>
                            {student.class}
                          </span> */}
                          <button
                            onClick={(e) => {
                              handleRemoveStudent(student, e);
                            }}
                            className='flex size-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                            aria-label={`Remove ${student.name}`}
                          >
                            <X className='size-3' />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </TabsContent>

        <TabsContent
          value={STUDENT_SELECTION_MODE.GROUP}
          className='space-y-4'
        >
          <SelectStudentByGroupTab
            selectedStudents={selectedStudents}
            setSelectedStudents={setSelectedStudents}
          />
        </TabsContent>

        <TabsContent
          value={STUDENT_SELECTION_MODE.TAG}
          className='space-y-4'
        >
          <SelectByTagTab
            onFamiliesResolved={(families) => {
              onFamiliesSelected?.(families);
            }}
          />
        </TabsContent>
      </Tabs>

      <div className='flex items-center justify-between pt-4'>
        <p className='text-sm text-muted-foreground'>
          {selectedStudents.length} Student(s) selected
        </p>
        <Button
          onClick={handleContinue}
          disabled={selectedStudents.length === 0}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
